// Drugs › Measured ΔFC (ba-web, with ba-activity's export): the twelve paired drug-minus-control changes in resting
// functional connectivity of sims/pharma/measured-delta-fc-v1 (#684, ba-pharma-connectivity-v0), read here from the
// run's own files as the lead's split has it (2026-10-02 23:50 UTC): connectivity.json and the small arrays eagerly,
// the vertex strength when the cortex is drawn, the 432 × 432 matrix lazily. The view is features/Dfc.tsx; where it
// is lives in `useDfc` and the bar (`view=dfc&condition=<id>`, src/app/url.ts). Parcels join by index or
// structure_id, never by name. The run stays out of index.json until ba-app's reader or skip rule, so it's opened by
// its path, and a machine without it gets null.

import { create } from 'zustand';
import { defaultSource, present, SimFiles, type SimSource } from '../data/sims/files.ts';

export const DFC_RUN = 'pharma/measured-delta-fc-v1';
export const DFC_LAYER = 'dfc';
/** The export's first primary condition. */
export const DEFAULT_CONDITION = 'psilocybin-allrest-nogsr';
export const DFC_CAPTION = 'Measured ΔFC · paired resting fMRI · Schaefer-400 + Tian S2';
/** The Methods sheet's first line while the view is on, in place of the course's. */
export const DFC_METHODS =
  'Measured change in resting functional connectivity: paired drug-minus-control contrasts from OpenNeuro resting fMRI, parcellated on Schaefer-400 and Tian S2 and shown on the cortex parcel by parcel (ba-activity, measured-delta-fc-v1).';
/** "5 people", "1 person". */
export const peopleText = (n: number): string => (n === 1 ? '1 person' : `${n} people`);

export interface DfcCondition {
  index: number;
  id: string;
  drug: string;
  dataset: string;
  sample: string;
  preprocessing: string;
  rest_selection: string;
  role: string;
  n_subjects: number;
  control: string;
  statistics: {
    mean_global_delta_fc_z: number;
    global_delta_fc_95ci: [number, number];
    mean_cortical_global_delta_fc_z?: number;
    cortical_global_delta_fc_95ci?: [number, number];
  };
  source_group?: string;
}

export interface DfcNode {
  index: number;
  label: number;
  name: string;
  component: string;
  surface_name?: string | null;
  structure_id: string;
}

interface ArrayDesc {
  path: string;
  dtype: string;
  shape: number[];
  byte_length?: number;
}

export interface Connectivity {
  format: string;
  conditions: DfcCondition[];
  nodes: DfcNode[];
  arrays: {
    delta_fc: ArrayDesc;
    edge_subject_count: ArrayDesc;
    parcel_strength: ArrayDesc;
    strength_edge_count: ArrayDesc;
    vertex_strength: ArrayDesc;
    vertex_parcel: ArrayDesc;
  };
  display: { matrix_symmetric_abs_limit: number; strength_symmetric_abs_limit: number; missing?: string };
  statistics?: Record<string, string>;
  comparability?: string;
  interpretation?: string;
  datasets?: Record<string, { version?: string; source?: string; reference?: string; processing?: string; sensitivity?: string }>;
  surface?: { space?: string; mesh?: string; vertices_per_hemisphere?: number; mapping?: string; subcortex?: string };
}

export interface DfcHeader {
  id?: string;
  methods?: string;
  model?: { name?: string; version?: string; source_note?: string };
  provenance?: string;
  source_measurement?: string;
}

/** The files the module reads: a run's SimFiles, or a stand-in in tests. */
export interface DfcFiles {
  json<T>(rel: string): Promise<T>;
  bytes(rel: string): Promise<ArrayBuffer>;
}

export interface Edge {
  node: DfcNode;
  dz: number;
  people: number;
}

export interface Ranked {
  node: DfcNode;
  dz: number;
  edges: number;
}

const product = (shape: readonly number[]) => shape.reduce((a, b) => a * b, 1);

function sized(buf: ArrayBuffer, desc: ArrayDesc, bytesPer: number, what: string): void {
  const want = product(desc.shape) * bytesPer;
  if (buf.byteLength !== want) throw new Error(`${what}: ${buf.byteLength} bytes, not the ${want} its shape [${desc.shape.join(', ')}] says`);
}

/** The export, with its small arrays in; the matrix and the vertex strengths come when asked, once. */
export class Dfc {
  private matrixP: Promise<{ dz: Float64Array; people: Uint16Array }> | null = null;
  private vertexP: Promise<Float32Array> | null = null;
  private parcelP: Promise<Int32Array> | null = null;
  /** Node indices on the cortex, in order. */
  readonly cortex: number[];

  constructor(
    readonly files: DfcFiles,
    readonly header: DfcHeader,
    readonly conn: Connectivity,
    private readonly strengthAll: Float64Array,
    private readonly edgesAll: Uint16Array,
  ) {
    this.cortex = conn.nodes.filter((n) => n.component === 'cortex').map((n) => n.index);
  }

  static async load(files: DfcFiles): Promise<Dfc> {
    const [header, conn] = await Promise.all([files.json<DfcHeader>('header.json'), files.json<Connectivity>('connectivity.json')]);
    const C = conn.conditions.length;
    const N = conn.nodes.length;
    const ps = conn.arrays.parcel_strength;
    const pe = conn.arrays.strength_edge_count;
    if (ps.shape[0] !== C || ps.shape[1] !== N) throw new Error(`parcel_strength is [${ps.shape.join(', ')}], not [${C}, ${N}]`);
    if (pe.shape[0] !== C || pe.shape[1] !== N) throw new Error(`strength_edge_count is [${pe.shape.join(', ')}], not [${C}, ${N}]`);
    const [s, e] = await Promise.all([files.bytes(ps.path), files.bytes(pe.path)]);
    sized(s, ps, 8, ps.path);
    sized(e, pe, 2, pe.path);
    return new Dfc(files, header, conn, new Float64Array(s), new Uint16Array(e));
  }

  get conditions(): DfcCondition[] {
    return this.conn.conditions;
  }
  get nodes(): DfcNode[] {
    return this.conn.nodes;
  }
  get n(): number {
    return this.conn.nodes.length;
  }
  /** The bars' half-ranges, Δz: one scale for every condition, as the export fixes them. */
  get limits(): { strength: number; matrix: number } {
    return { strength: this.conn.display.strength_symmetric_abs_limit, matrix: this.conn.display.matrix_symmetric_abs_limit };
  }

  /** The condition an id names; the first (primary) when it names none. */
  condition(id: string | null | undefined): DfcCondition {
    return this.conn.conditions.find((c) => c.id === id) ?? this.conn.conditions[0]!;
  }

  /** A condition's mean change over each parcel's available edges (Δz), by node index. */
  strength(c: number): Float64Array {
    return this.strengthAll.subarray(c * this.n, (c + 1) * this.n);
  }
  /** How many edges each parcel's mean stands on. */
  edges(c: number): Uint16Array {
    return this.edgesAll.subarray(c * this.n, (c + 1) * this.n);
  }
  /** Whether a parcel has a measurement in a condition: an edge count above zero and a finite mean. */
  measured(c: number, i: number): boolean {
    return (this.edges(c)[i] ?? 0) > 0 && Number.isFinite(this.strength(c)[i]);
  }
  /** Cortical parcels with a measurement, of the cortex's count: the export's 377–400. */
  finiteCortical(c: number): { measured: number; of: number } {
    let measured = 0;
    for (const i of this.cortex) if (this.measured(c, i)) measured++;
    return { measured, of: this.cortex.length };
  }
  /** The measured parcels by the size of their change, largest first. */
  ranked(c: number, n = 10): Ranked[] {
    const s = this.strength(c);
    const e = this.edges(c);
    const out: Ranked[] = [];
    for (const node of this.conn.nodes) if (this.measured(c, node.index)) out.push({ node, dz: s[node.index]!, edges: e[node.index]! });
    out.sort((a, b) => Math.abs(b.dz) - Math.abs(a.dz));
    return out.slice(0, n);
  }

  /** A condition's strength expanded to the fsLR-32k vertices, left then right (NaN off the parcels and where unmeasured). */
  vertexStrength(c: number): Promise<Float32Array> {
    const d = this.conn.arrays.vertex_strength;
    this.vertexP ??= this.files.bytes(d.path).then((buf) => {
      sized(buf, d, 4, d.path);
      return new Float32Array(buf);
    });
    const V = d.shape[1]!;
    return this.vertexP.then((all) => all.subarray(c * V, (c + 1) * V));
  }

  /** The parcel of every fsLR-32k vertex, left then right, −1 on the medial wall (`vertex-parcel.i32`), read once. */
  vertexParcel(): Promise<Int32Array> {
    const d = this.conn.arrays.vertex_parcel;
    this.parcelP ??= this.files.bytes(d.path).then((buf) => {
      sized(buf, d, 4, d.path);
      return new Int32Array(buf);
    });
    return this.parcelP;
  }

  /** The whole matrix and the people behind each edge, read once (the export's largest files). */
  matrix(): Promise<{ dz: Float64Array; people: Uint16Array }> {
    const dd = this.conn.arrays.delta_fc;
    const dp = this.conn.arrays.edge_subject_count;
    this.matrixP ??= Promise.all([this.files.bytes(dd.path), this.files.bytes(dp.path)]).then(([a, b]) => {
      sized(a, dd, 8, dd.path);
      sized(b, dp, 2, dp.path);
      return { dz: new Float64Array(a), people: new Uint16Array(b) };
    });
    return this.matrixP;
  }

  /** One edge of a condition: its change and the people it stands on; null where the export has none (NaN, or no one). */
  async edge(c: number, i: number, j: number): Promise<Edge | null> {
    const { dz, people } = await this.matrix();
    const k = (c * this.n + i) * this.n + j;
    const v = dz[k]!;
    const p = people[k]!;
    return Number.isFinite(v) && p > 0 ? { node: this.conn.nodes[j]!, dz: v, people: p } : null;
  }

  /** A parcel's largest changed edges in a condition, from the matrix row. */
  async strongestEdges(c: number, i: number, n = 3): Promise<Edge[]> {
    const { dz, people } = await this.matrix();
    const row = (c * this.n + i) * this.n;
    const out: Edge[] = [];
    for (let j = 0; j < this.n; j++) {
      if (j === i) continue;
      const v = dz[row + j]!;
      const p = people[row + j]!;
      if (Number.isFinite(v) && p > 0) out.push({ node: this.conn.nodes[j]!, dz: v, people: p });
    }
    out.sort((a, b) => Math.abs(b.dz) - Math.abs(a.dz));
    return out.slice(0, n);
  }
}

const DRUG: Record<string, string> = { psilocybin: 'Psilocybin', methylphenidate: 'Methylphenidate', lsd: 'LSD' };
const PREPROCESSING: Record<string, string> = { nogsr: 'no added GSR', gsr: 'GSR', localwm: 'local WM regression' };
const REST: Record<string, string> = { allrest: 'all rest', premusic: 'before music', postmusic: 'after music' };

const capital = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

export const drugLabel = (drug: string): string => DRUG[drug] ?? capital(drug);

/** A condition's preprocessing and rest selection, as people read them: "no added GSR · all rest". */
export function variantLabel(c: Pick<DfcCondition, 'preprocessing' | 'rest_selection'>): string {
  return `${PREPROCESSING[c.preprocessing] ?? c.preprocessing} · ${REST[c.rest_selection] ?? c.rest_selection}`;
}

export const conditionLabel = (c: DfcCondition): string => `${drugLabel(c.drug)} · ${variantLabel(c)}`;

/** A parcel's name without the atlas's prefix and underscores: 7Networks_LH_Vis_1 → LH Vis 1. */
export const parcelName = (node: Pick<DfcNode, 'name'>): string => node.name.replace(/^7Networks_/, '').replace(/_/g, ' ');

export interface Network {
  /** Schaefer's token in the parcel name: Vis, SomMot, DorsAttn, SalVentAttn, Limbic, Cont, Default. */
  id: string;
  name: string;
  /** The atlas's own colour for the network (Yeo 2011's 7-network LUT). */
  rgb: [number, number, number];
}

/** Yeo 2011's seven networks in Schaefer's order, which is the order the export's cortical rows run in within each hemisphere. */
export const NETWORKS: Network[] = [
  { id: 'Vis', name: 'Visual', rgb: [120, 18, 134] },
  { id: 'SomMot', name: 'Somatomotor', rgb: [70, 130, 180] },
  { id: 'DorsAttn', name: 'Dorsal attention', rgb: [0, 118, 14] },
  { id: 'SalVentAttn', name: 'Salience / ventral attention', rgb: [196, 58, 250] },
  { id: 'Limbic', name: 'Limbic', rgb: [220, 248, 164] },
  { id: 'Cont', name: 'Control', rgb: [230, 148, 34] },
  { id: 'Default', name: 'Default mode', rgb: [205, 62, 78] },
];

/** A cortical parcel's network from its Schaefer name (7Networks_LH_Vis_1 → Visual); null for the subcortex or an unknown token. */
export function networkOf(node: Pick<DfcNode, 'name' | 'component'>): Network | null {
  if (node.component !== 'cortex') return null;
  const m = /^7Networks_(?:LH|RH)_([A-Za-z]+)_/.exec(node.name);
  return (m && NETWORKS.find((n) => n.id === m[1])) ?? null;
}

export type Hemi = 'LH' | 'RH' | 'Sub';

/** A run of the export's rows in one hemisphere and network (or the subcortex), `from` to `to` inclusive. */
export interface NetworkBand {
  hemi: Hemi;
  network: Network | null;
  from: number;
  to: number;
}

const hemiOf = (node: Pick<DfcNode, 'name' | 'component'>): Hemi => (node.component !== 'cortex' ? 'Sub' : node.name.includes('_RH_') ? 'RH' : 'LH');

/** The export's rows as bands: LH's seven networks, RH's seven, then the subcortex, each a contiguous run in the export's order. */
export function networkBands(nodes: Pick<DfcNode, 'index' | 'name' | 'component'>[]): NetworkBand[] {
  const out: NetworkBand[] = [];
  for (const n of nodes) {
    const hemi = hemiOf(n);
    const network = networkOf(n);
    const last = out[out.length - 1];
    if (last && last.hemi === hemi && last.network === network) last.to = n.index;
    else out.push({ hemi, network, from: n.index, to: n.index });
  }
  return out;
}

/** What a band is called in a tooltip or the readout: "LH Visual · 31 parcels", "Subcortex · 32 regions". */
export function bandLabel(b: NetworkBand): string {
  const n = b.to - b.from + 1;
  return b.hemi === 'Sub' ? `Subcortex (Tian S2) · ${n} regions` : `${b.hemi} ${b.network?.name ?? 'cortex'} · ${n} parcels`;
}

/** A signed Δz with its sign always written, a true minus for the negative. */
export function dzText(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return 'No measurement';
  const a = Math.abs(v).toFixed(digits);
  return v < 0 && Number(a) !== 0 ? `−${a}` : v > 0 && Number(a) !== 0 ? `+${a}` : a;
}

/** A 95 % interval written out: "0.007 to 0.230". */
export const intervalText = (ci: [number, number], digits = 3): string => `${dzText(ci[0], digits)} to ${dzText(ci[1], digits)}`;

let opening: Promise<Dfc | null> | null = null;

/** The export on this machine, read once; null where sims/ has no such run. */
export function openDfc(src: SimSource = defaultSource()): Promise<Dfc | null> {
  opening ??= (async () => {
    if (!(await present(src, `${DFC_RUN}/connectivity.json`))) return null;
    return Dfc.load(new SimFiles(src, DFC_RUN));
  })().catch((err: unknown) => {
    opening = null;
    throw err;
  });
  return opening;
}

export type LayerState = 'off' | 'pushed' | 'none';

interface DfcState {
  /** The condition shown, by id (the bar's `condition=`). */
  condition: string;
  /** The parcel under inspection, by node index. */
  parcel: number | null;
  /** The export: undefined until looked for, null where this machine hasn't it. */
  data: Dfc | null | undefined;
  /** Whether the cortex strength reached the viewport's live layer; `none` on one without it (the 2D preview). */
  layer: LayerState;
  covered: { vertices: number; covered: number; of: number } | null;
  /** The matrix sheet (features/DfcMatrix.tsx), `matrix=1` in the bar. */
  matrixOpen: boolean;
  setCondition(id: string): void;
  setMatrixOpen(open: boolean): void;
  setParcel(i: number | null): void;
  setData(d: Dfc | null): void;
  setLayer(l: LayerState, covered?: { vertices: number; covered: number; of: number } | null): void;
}

export const useDfc = create<DfcState>()((set) => ({
  condition: DEFAULT_CONDITION,
  parcel: null,
  data: undefined,
  layer: 'off',
  covered: null,
  matrixOpen: false,
  setCondition: (condition) => set({ condition }),
  setMatrixOpen: (matrixOpen) => set({ matrixOpen }),
  setParcel: (parcel) => set({ parcel }),
  setData: (data) => set({ data }),
  setLayer: (layer, covered = null) => set({ layer, covered }),
}));

// The checks read the view's state here, as they read the app's at window.__atlas.
if (typeof window !== 'undefined') (window as unknown as { __dfc?: typeof useDfc }).__dfc = useDfc;
